import { useContext, useState } from 'react';
import styled, { ThemeProvider, css } from 'styled-components';
import { redTheme } from 'src/styles/theme';
import { EventCountdown } from 'src/modules/events/components/EventCountdown';
import { media } from 'src/styles/designs';
import { useTranslate } from 'react-t7e';
import { H1 } from 'src/components/common/typography/Header';
import { useFlattenEvent } from 'src/modules/events/hooks/useFlattenEvent';
import Share from 'src/components/icons/Share';
import { Button } from 'src/components/common/Button';
import { casePathEmptyString } from 'src/utils/casePathEmptyString';
import { EventPageContext } from 'src/modules/events/components/EventPage/EventPageContext';
import { EventPageHeaderContentDate } from 'src/modules/events/components/EventPage/EventPageHeaderSection/EventPageHeaderContent/EventPageHeaderContentDate';
import { AnimatePresence } from 'framer-motion';
import { ShareOverlay } from 'src/components/common/ShareOverlay';
import { DirectusImage } from 'src/components/common/DirectusImage';
import { EventPageHeaderContentCTAs } from 'src/modules/events/components/EventPage/EventPageHeaderSection/EventPageHeaderContent/EventPageHeaderContentCTAs';

const Container = styled.div<{ $short: boolean }>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-top: 10px;
  padding-bottom: 30px;
  color: ${({ theme }) => theme.text.palette.primary};
  ${media.w.greaterThanOrEqual.desktop} {
    padding-bottom: 70px;
    ${casePathEmptyString('$short')(
      css`
        padding-bottom: 0 !important;
      `,
    )}
  }
`;

const StyledEventCountdown = styled(EventCountdown)`
  &&& {
    width: 100%;
    display: grid;
    position: static;
    bottom: auto;
    right: auto;
    transform: none;

    ${media.w.lessThan.desktop} {
      display: flex;
      justify-content: flex-end;
    }
  }
`;

const Content = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding-bottom: 18px;
  gap: 20px;
  ${media.w.greaterThanOrEqual.desktop} {
    flex-direction: row;
    padding-bottom: 24px;
    align-items: center;
  }
`;

const Heading = styled(H1)`
  font-size: 32px;
  line-height: 32px;
  font-weight: 300;
  max-width: 270px;
  margin: 0;
  padding-left: 0;
  span {
    display: block;
    font-weight: 250;
  }
  &:before {
    content: none;
  }
  ${media.w.greaterThanOrEqual.desktop} {
    letter-spacing: 0.02em;
    line-height: 36px;
    max-width: 450px;
    padding-left: 46px;
    &:before {
      content: '';
      top: 50%;
      transform: translateY(-50%);
    }
  }
`;

const HeadingContainer = styled.div<{ $centerItems: boolean }>`
  overflow-wrap: break-word;
  display: flex;
  align-items: ${({ $centerItems }) =>
    $centerItems ? 'center' : 'flex-start'};
  gap: 15px;
  ${media.w.lessThan.desktop} {
    gap: 9px;
  }
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FormContainer = styled.div`
  display: flex;
  gap: 11px;
  font-size: 12px;
  line-height: 21px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ReachText = styled.div`
  font-weight: 300;
  letter-spacing: 1px;
  opacity: 0.7;
`;

const StatusText = styled.div`
  font-weight: bold;
  letter-spacing: 0.08em;
  font-size: 21px;
  line-height: 21px;
  text-transform: uppercase;
  margin-bottom: 10px;
  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 0;
    font-weight: 300;
    font-size: 32px;
  }
`;

const LogoContainer = styled.div`
  position: relative;
  width: 122px;
  height: 45px;
  margin-bottom: 36px;
  ${media.w.greaterThanOrEqual.desktop} {
    width: 162px;
    height: 64px;
  }
`;

export const EventPageHeaderContent = () => {
  const { _ } = useTranslate();
  const [shareOverlay, setShareOverlay] = useState(false);

  const { event, isUpcoming, isCompleted } = useContext(EventPageContext);

  const flatEvent = useFlattenEvent(event);

  return (
    <ThemeProvider theme={redTheme}>
      <AnimatePresence>
        {shareOverlay && (
          <ShareOverlay titles close={() => setShareOverlay(false)} />
        )}
      </AnimatePresence>

      <Container $short={!isUpcoming}>
        <Content>
          {isUpcoming && flatEvent.logo && (
            <LogoContainer>
              <DirectusImage
                src={flatEvent.logo.id}
                layout="fill"
                objectFit="contain"
                objectPosition="0 50%"
              />
            </LogoContainer>
          )}

          <Header>
            <Details>
              <FormContainer>
                <div>{flatEvent.form}</div>
                {flatEvent.reach && (
                  <>
                    <ReachText>|</ReachText>
                    <ReachText>{flatEvent.reach}</ReachText>
                  </>
                )}
              </FormContainer>
              {!isUpcoming ? (
                <StatusText>{_(isCompleted ? 'ended' : 'now')}</StatusText>
              ) : (
                <EventPageHeaderContentDate />
              )}
            </Details>

            <HeadingContainer $centerItems={!flatEvent.type?.name}>
              <Heading>
                {flatEvent.type?.name}
                <span>{flatEvent.name}</span>
              </Heading>
              <Button
                onClick={() => setShareOverlay(true)}
                kind="circled"
                size="small"
                icon={Share}
              />
            </HeadingContainer>
          </Header>

          <EventPageHeaderContentCTAs />
        </Content>

        <StyledEventCountdown event={event} />
      </Container>
    </ThemeProvider>
  );
};
